<script setup>
import { computed, onMounted, ref } from 'vue';

const campaigns = ref([]);
const services = ref([]);
const establishments = ref([]);
const selectedId = ref('');
const activeTab = ref('details');
const isLoading = ref(false);
const isSaving = ref(false);
const message = ref('');
const form = ref(emptyForm());

function localDateTime(offsetDays = 0) {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function emptyOffer() {
  return {
    name: '',
    promotionalPrice: '',
    currency: 'EUR',
    priority: 0,
    description: '',
    salesPitch: '',
    closingQuestion: 'Quer que eu veja um horario para voce?',
    terms: '',
    serviceIds: [],
    isActive: true,
  };
}

function emptyForm() {
  return {
    name: '',
    slug: '',
    status: 'draft',
    startsAt: localDateTime(),
    endsAt: localDateTime(30),
    timezone: 'Europe/Lisbon',
    priority: 0,
    headline: '',
    description: '',
    terms: '',
    isAllEstablishments: true,
    establishmentIds: [],
    offers: [emptyOffer()],
  };
}

const selected = computed(() => campaigns.value.find(item => item.id === selectedId.value));

function priceCents(value) {
  if (value === '' || value === null || value === undefined) return null;
  return Math.round(Number(value) * 100);
}

function formatPrice(cents, currency = 'EUR') {
  return new Intl.NumberFormat('pt-PT', { style: 'currency', currency }).format((cents || 0) / 100);
}

function formatPeriod(item) {
  return `${new Date(item.starts_at).toLocaleDateString('pt-PT')} - ${new Date(item.ends_at).toLocaleDateString('pt-PT')}`;
}

function toLocalDateTime(value) {
  const date = new Date(value);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 16);
}

function payload() {
  return {
    ...form.value,
    offers: form.value.offers.map(offer => ({
      ...offer,
      promotionalPriceCents: priceCents(offer.promotionalPrice),
    })),
  };
}

async function load() {
  isLoading.value = true;
  message.value = '';
  try {
    const [offersResponse, servicesResponse, establishmentsResponse] = await Promise.all([
      fetch('/api/superwoot/commercial-offers'),
      fetch('/api/superwoot/services'),
      fetch('/api/superwoot/establishments?includeInactive=true'),
    ]);
    const [offersPayload, servicesPayload, establishmentsPayload] = await Promise.all([
      offersResponse.json(),
      servicesResponse.json(),
      establishmentsResponse.json(),
    ]);
    if (!offersResponse.ok) throw new Error(offersPayload.error || 'Falha ao carregar ofertas');
    if (!servicesResponse.ok) throw new Error(servicesPayload.error || 'Falha ao carregar servicos');
    if (!establishmentsResponse.ok) throw new Error(establishmentsPayload.error || 'Falha ao carregar unidades');
    campaigns.value = offersPayload.commercialOffers || [];
    services.value = servicesPayload.services || [];
    establishments.value = establishmentsPayload.establishments || [];
  } catch (error) {
    message.value = error.message;
  } finally {
    isLoading.value = false;
  }
}

function createNew() {
  selectedId.value = '';
  activeTab.value = 'details';
  form.value = emptyForm();
}

function select(item) {
  selectedId.value = item.id;
  activeTab.value = 'details';
  form.value = {
    name: item.name || '',
    slug: item.slug || '',
    status: item.status || 'draft',
    startsAt: toLocalDateTime(item.starts_at),
    endsAt: toLocalDateTime(item.ends_at),
    timezone: item.timezone || 'Europe/Lisbon',
    priority: item.priority || 0,
    headline: item.headline || '',
    description: item.description || '',
    terms: item.terms || '',
    isAllEstablishments: Boolean(item.is_all_establishments),
    establishmentIds: (item.commercial_campaign_establishments || []).map(link => link.establishment_id),
    offers: (item.commercial_offers || []).map(offer => ({
      name: offer.name || '',
      promotionalPrice: offer.promotional_price_cents / 100,
      currency: offer.currency || 'EUR',
      priority: offer.priority || 0,
      description: offer.description || '',
      salesPitch: offer.sales_pitch || '',
      closingQuestion: offer.closing_question || '',
      terms: offer.terms || '',
      serviceIds: (offer.commercial_offer_services || []).map(link => link.service_id),
      isActive: Boolean(offer.is_active),
    })),
  };
  if (!form.value.offers.length) form.value.offers = [emptyOffer()];
}

function addOffer() {
  form.value.offers.push(emptyOffer());
}

function removeOffer(index) {
  if (form.value.offers.length === 1) return;
  form.value.offers.splice(index, 1);
}

async function save() {
  isSaving.value = true;
  message.value = '';
  try {
    const body = payload();
    if (selectedId.value) body.campaignId = selectedId.value;
    const response = await fetch('/api/superwoot/commercial-offers', {
      method: selectedId.value ? 'PATCH' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.error || 'Falha ao salvar oferta comercial');
    message.value = 'Oferta comercial salva.';
    const id = selectedId.value || result.commercialOffer?.id;
    await load();
    const saved = campaigns.value.find(item => item.id === id);
    if (saved) select(saved);
  } catch (error) {
    message.value = error.message;
  } finally {
    isSaving.value = false;
  }
}

onMounted(load);
</script>

<template>
  <div class="flex h-full min-h-0 flex-col bg-n-background">
    <header class="border-b border-n-weak px-4 py-4 sm:px-6">
      <div class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="text-xl font-medium text-n-slate-12">Ofertas comerciais</h1>
          <p class="mt-1 text-sm text-n-slate-10">Condicoes sazonais e combos usados pelo atendimento inteligente.</p>
        </div>
        <button class="inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white" type="button" @click="createNew">
          <span class="i-lucide-plus size-4" />
          Nova oferta
        </button>
      </div>
    </header>

    <div class="grid min-h-0 flex-1 lg:grid-cols-[minmax(260px,360px)_1fr]">
      <aside class="border-b border-n-weak p-4 lg:border-b-0 lg:border-r">
        <p v-if="isLoading" class="text-sm text-n-slate-10">Carregando...</p>
        <p v-else-if="campaigns.length === 0" class="text-sm text-n-slate-10">Nenhuma oferta cadastrada.</p>
        <div class="space-y-2">
          <button v-for="item in campaigns" :key="item.id" type="button" class="w-full rounded-md border p-3 text-left" :class="item.id === selectedId ? 'border-n-brand bg-n-alpha-2' : 'border-n-weak hover:bg-n-alpha-1'" @click="select(item)">
            <span class="flex items-center justify-between gap-2">
              <strong class="text-sm text-n-slate-12">{{ item.name }}</strong>
              <span class="text-xs capitalize text-n-slate-10">{{ item.status }}</span>
            </span>
            <span class="mt-1 block text-xs text-n-slate-10">{{ formatPeriod(item) }}</span>
            <span class="mt-1 block text-xs text-n-slate-10">{{ item.commercial_offers?.length || 0 }} oferta(s)</span>
          </button>
        </div>
      </aside>

      <main class="overflow-y-auto p-4 sm:p-6">
        <form class="mx-auto max-w-3xl" @submit.prevent="save">
          <h2 class="text-base font-medium text-n-slate-12">{{ selected ? 'Editar oferta comercial' : 'Nova oferta comercial' }}</h2>
          <p class="mt-1 text-sm text-n-slate-10">A IA recebe somente ofertas ativas e dentro da vigencia.</p>

          <nav class="mt-5 flex gap-1 overflow-x-auto border-b border-n-weak">
            <button v-for="tab in [{ id: 'details', label: 'Dados' }, { id: 'period', label: 'Vigencia' }, { id: 'units', label: 'Unidades' }, { id: 'offers', label: 'Combos e precos' }]" :key="tab.id" type="button" class="border-b-2 px-3 py-2 text-sm" :class="activeTab === tab.id ? 'border-n-brand text-n-slate-12' : 'border-transparent text-n-slate-10'" @click="activeTab = tab.id">
              {{ tab.label }}
            </button>
          </nav>

          <section v-if="activeTab === 'details'" class="mt-5 space-y-4">
            <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Nome interno</span><input v-model="form.name" required class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
            <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Chamada comercial</span><input v-model="form.headline" class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Condicao especial deste mes" /></label>
            <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Descricao</span><textarea v-model="form.description" rows="3" class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" /></label>
            <label class="block"><span class="mb-1 block text-sm text-n-slate-11">Condicoes gerais</span><textarea v-model="form.terms" rows="2" class="w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" /></label>
          </section>

          <section v-else-if="activeTab === 'period'" class="mt-5 grid gap-4 sm:grid-cols-2">
            <label><span class="mb-1 block text-sm text-n-slate-11">Inicio</span><input v-model="form.startsAt" type="datetime-local" required class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
            <label><span class="mb-1 block text-sm text-n-slate-11">Fim</span><input v-model="form.endsAt" type="datetime-local" required class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
            <label><span class="mb-1 block text-sm text-n-slate-11">Status</span><select v-model="form.status" class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm"><option value="draft">Rascunho</option><option value="active">Ativa</option><option value="paused">Pausada</option><option value="ended">Encerrada</option></select></label>
            <label><span class="mb-1 block text-sm text-n-slate-11">Prioridade</span><input v-model.number="form.priority" type="number" min="0" max="1000" class="h-10 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" /></label>
          </section>

          <section v-else-if="activeTab === 'units'" class="mt-5 space-y-3">
            <label class="flex items-center gap-2 text-sm text-n-slate-11"><input v-model="form.isAllEstablishments" type="checkbox" /> Usar em todos os estabelecimentos</label>
            <div v-if="!form.isAllEstablishments" class="grid gap-2 sm:grid-cols-2">
              <label v-for="item in establishments.filter(unit => unit.is_active)" :key="item.id" class="flex items-center gap-2 rounded-md border border-n-weak p-3 text-sm text-n-slate-11"><input v-model="form.establishmentIds" type="checkbox" :value="item.id" /> {{ item.name }}</label>
            </div>
          </section>

          <section v-else class="mt-5 space-y-4">
            <article v-for="(offer, index) in form.offers" :key="index" class="rounded-md border border-n-weak p-4">
              <div class="flex items-center justify-between gap-3"><h3 class="text-sm font-medium text-n-slate-12">Oferta {{ index + 1 }}</h3><button type="button" title="Remover oferta" class="i-lucide-trash-2 size-4 text-n-ruby-9" @click="removeOffer(index)" /></div>
              <div class="mt-3 grid gap-3 sm:grid-cols-3">
                <input v-model="offer.name" required class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm sm:col-span-2" placeholder="Nome do combo" />
                <input v-model="offer.promotionalPrice" required type="number" min="0" step="0.01" class="h-9 rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Preco promocional" />
              </div>
              <div class="mt-3 grid gap-2 sm:grid-cols-2">
                <label v-for="service in services.filter(item => item.is_active)" :key="service.id" class="flex items-center gap-2 text-sm text-n-slate-11"><input v-model="offer.serviceIds" type="checkbox" :value="service.id" /> {{ service.name }}</label>
              </div>
              <textarea v-model="offer.description" rows="2" class="mt-3 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" placeholder="O que esta incluso" />
              <textarea v-model="offer.salesPitch" rows="2" class="mt-3 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm" placeholder="Beneficio para apresentar ao lead" />
              <input v-model="offer.closingQuestion" class="mt-3 h-9 w-full rounded-md border border-n-weak bg-n-alpha-1 px-3 text-sm" placeholder="Pergunta curta para fechamento" />
            </article>
            <button type="button" class="inline-flex items-center gap-2 text-sm font-medium text-n-brand" @click="addOffer"><span class="i-lucide-plus size-4" />Adicionar combo ou preco</button>
          </section>

          <p v-if="message" class="mt-4 text-sm text-n-slate-11">{{ message }}</p>
          <button class="mt-5 inline-flex h-9 items-center gap-2 rounded-md bg-n-brand px-3 text-sm font-medium text-white disabled:opacity-60" type="submit" :disabled="isSaving">
            <span class="i-lucide-save size-4" />
            {{ isSaving ? 'Salvando...' : 'Salvar oferta comercial' }}
          </button>
        </form>
      </main>
    </div>
  </div>
</template>
